import React, { useState, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Text, Group, Badge, Paper, Box, Menu } from '@mantine/core';
import { DotsSixVertical, GoogleLogo, MetaLogo, Rows, X, CaretDown } from '@phosphor-icons/react';
import api from '../utils/api';

function campaignTypeLabel(c, t) {
  if (!c) return '';
  if (c.platform === 'meta_ads') return c.campaign_goal === 'leads' ? t('campaignEdit.reorder.types.metaLeads') : t('campaignEdit.reorder.types.metaTraffic');
  if (c.platform === 'microsoft_ads') return t('campaignEdit.reorder.types.microsoft');
  const type = c.campaign_type;
  if (type === 'shopping') return t('campaignEdit.reorder.types.googleShopping');
  if (type === 'performance_max' || type === 'pmax') return t('campaignEdit.reorder.types.googlePmax');
  if (type === 'display') return t('campaignEdit.reorder.types.googleDisplay');
  if (type === 'video') return t('campaignEdit.reorder.types.googleVideo');
  return t('campaignEdit.reorder.types.googleSearch');
}

function SortableItem({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: 'relative',
    zIndex: isDragging ? 10 : 0,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <div className="flex items-center gap-2">
        <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500 shrink-0 p-1">
          <DotsSixVertical size={16} weight="bold" />
        </div>
        <div className="flex-1 min-w-0">
          {children}
        </div>
      </div>
    </div>
  );
}

const CampaignReorder = forwardRef(function CampaignReorder({ campaigns, groups, onSave, onCancel, onError }, ref) {
  const { t } = useTranslation();
  // Each item has an explicit groupId (not inferred from position)
  const buildItems = useCallback(() => {
    const items = [];
    const sortedGroups = [...groups].sort((a, b) => a.position - b.position);
    for (const g of sortedGroups) {
      items.push({ id: `group:${g.id}`, type: 'group', group: g });
      const gc = campaigns
        .filter(c => c.group === g.id)
        .sort((a, b) => (a.position || 0) - (b.position || 0));
      for (const c of gc) {
        items.push({ id: `c:${c.id}`, type: 'campaign', campaign: c, groupId: g.id });
      }
    }
    const ungrouped = campaigns
      .filter(c => !c.group)
      .sort((a, b) => (a.position || 0) - (b.position || 0));
    for (const c of ungrouped) {
      items.push({ id: `c:${c.id}`, type: 'campaign', campaign: c, groupId: null });
    }
    return items;
  }, [campaigns, groups]);

  const [items, setItems] = useState(buildItems);
  const [saving, setSaving] = useState(false);
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // After reorder, infer the groupId for the moved campaign from its neighbor above
  const inferGroupId = (arr, index) => {
    for (let i = index - 1; i >= 0; i--) {
      if (arr[i].type === 'group') return arr[i].group.id;
      if (arr[i].type === 'campaign') return arr[i].groupId;
    }
    return null; // above all groups = ungrouped
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setItems(prev => {
      const oldIndex = prev.findIndex(i => i.id === active.id);
      const newIndex = prev.findIndex(i => i.id === over.id);
      const moved = arrayMove(prev, oldIndex, newIndex);

      // Update the groupId of the moved item based on its new neighbors
      const movedItem = moved[newIndex];
      if (movedItem.type === 'campaign') {
        movedItem.groupId = inferGroupId(moved, newIndex);
      }

      return [...moved];
    });
  };

  // Move campaign to a specific group via menu
  const handleMoveCampaign = (campaignId, targetGroupId) => {
    setItems(prev => {
      const next = prev.map(i => ({ ...i }));
      const campIdx = next.findIndex(i => i.type === 'campaign' && i.campaign.id === campaignId);
      if (campIdx === -1) return prev;

      // Update groupId
      next[campIdx].groupId = targetGroupId;

      // Move the item to the right position
      const [campItem] = next.splice(campIdx, 1);

      if (targetGroupId) {
        // Find group header, insert after last campaign in that group
        const groupIdx = next.findIndex(i => i.type === 'group' && i.group.id === targetGroupId);
        if (groupIdx === -1) return prev;
        let insertIdx = groupIdx + 1;
        while (insertIdx < next.length && next[insertIdx].type === 'campaign' && next[insertIdx].groupId === targetGroupId) {
          insertIdx++;
        }
        next.splice(insertIdx, 0, campItem);
      } else {
        // Move to ungrouped: append at end
        next.push(campItem);
      }
      return next;
    });
  };

  const handleSave = useCallback(async () => {
    setSaving(true);
    const currentItems = itemsRef.current;

    // Build from explicit groupIds, with global positions for interleaving
    const groupsData = {};
    const groupOrder = [];
    const ungrouped = [];
    let globalPos = 0;

    for (const item of currentItems) {
      if (item.type === 'group') {
        groupsData[item.group.id] = {
          id: item.group.id,
          position: globalPos++,
          campaign_ids: [],
        };
        groupOrder.push(item.group.id);
      } else if (item.type === 'campaign') {
        if (item.groupId && groupsData[item.groupId]) {
          groupsData[item.groupId].campaign_ids.push(item.campaign.id);
        } else {
          // Ungrouped: send {id, position} with global position
          ungrouped.push({ id: item.campaign.id, position: globalPos++ });
        }
      }
    }

    try {
      await api.post('/ads/campaign-groups/reorder/', {
        groups: groupOrder.map(gid => groupsData[gid]),
        ungrouped,
      });
      onSave();
    } catch (err) {
      if (onError) onError(t('campaignEdit.reorder.errorSave'));
    }
    setSaving(false);
  }, [onSave, onError, t]);

  useImperativeHandle(ref, () => ({ save: handleSave }), [handleSave]);

  const itemIds = items.map(i => i.id);

  return (
    <Paper withBorder radius="md" style={{ overflow: 'hidden' }}>
      <Box px="md" py="sm" style={{ borderBottom: '1px solid var(--mantine-color-gray-2)', backgroundColor: 'var(--mantine-color-gray-0)' }}>
        <Text size="sm" fw={600}>{t('campaignEdit.reorder.instructions')}</Text>
      </Box>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
          {items.map((item, idx) => {
            if (item.type === 'group') {
              const count = items.filter(i => i.type === 'campaign' && i.groupId === item.group.id).length;
              return (
                <SortableItem key={item.id} id={item.id}>
                  <Group gap="xs" wrap="nowrap" style={{ minHeight: 28 }}>
                    <Rows size={14} className="text-indigo-500" />
                    <Text size="sm" fw={600} c="indigo.7">{item.group.name}</Text>
                    <Badge size="xs" variant="light" color="indigo">{count}</Badge>
                  </Group>
                </SortableItem>
              );
            }

            const campaign = item.campaign;
            const groupId = item.groupId;
            const inGroup = !!groupId;
            const PlatformIcon = campaign.platform === 'meta_ads' ? MetaLogo : GoogleLogo;
            const platformColor = campaign.platform === 'meta_ads' ? '#1877F2' : '#4285F4';
            const availableGroups = groups.filter(g => g.id !== groupId);
            const currentGroupName = groupId ? groups.find(g => g.id === groupId)?.name : null;

            // Show "Bez grupy" separator before first ungrouped campaign
            const prevItem = idx > 0 ? items[idx - 1] : null;
            const showUngroupedHeader = !inGroup && groups.length > 0 && (
              !prevItem || prevItem.type === 'group' || (prevItem.type === 'campaign' && prevItem.groupId)
            );

            return (
              <div key={item.id}>
                {showUngroupedHeader && (
                  <div className="px-4 py-1.5 bg-gray-50 border-t border-gray-200">
                    <Text size="xs" fw={500} c="dimmed">{t('campaignEdit.reorder.ungrouped')}</Text>
                  </div>
                )}
                <div style={inGroup ? { marginLeft: 12, borderLeft: '3px solid var(--mantine-color-indigo-2)', paddingLeft: 4 } : undefined}>
                  <div className="py-1 px-2 border-b border-gray-50 hover:bg-gray-50 transition">
                    <SortableItem id={item.id}>
                      <Group justify="space-between" wrap="nowrap" gap="xs" style={{ minHeight: 30 }}>
                        <Group gap="xs" wrap="nowrap" style={{ flex: 1, minWidth: 0 }} align="flex-start">
                          <PlatformIcon size={14} weight="bold" style={{ color: platformColor, flexShrink: 0, marginTop: 2 }} />
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <Group gap={6} wrap="nowrap">
                              <Text size="sm" truncate>{campaign.name}</Text>
                              {campaign.is_managed && (
                                <Badge size="xs" variant="light" color="green" style={{ flexShrink: 0 }}>Droid</Badge>
                              )}
                            </Group>
                            <Text size="10px" c="dimmed" truncate>{campaignTypeLabel(campaign, t)}</Text>
                          </div>
                        </Group>
                        <Menu shadow="sm" position="bottom-end" withArrow withinPortal>
                          <Menu.Target>
                            <button className="flex items-center gap-1 px-2 py-0.5 rounded text-xs text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 transition shrink-0">
                              {currentGroupName ? (
                                <span className="text-indigo-400">{currentGroupName}</span>
                              ) : (
                                <span>{t('campaignEdit.reorder.ungrouped')}</span>
                              )}
                              <CaretDown size={10} />
                            </button>
                          </Menu.Target>
                          <Menu.Dropdown>
                            <Menu.Label>{t('campaignEdit.reorder.moveTo')}</Menu.Label>
                            {availableGroups.map(g => (
                              <Menu.Item key={g.id} leftSection={<Rows size={14} className="text-indigo-500" />} onClick={() => handleMoveCampaign(campaign.id, g.id)}>
                                {g.name}
                              </Menu.Item>
                            ))}
                            {groupId && (
                              <Menu.Item leftSection={<X size={14} className="text-gray-400" />} onClick={() => handleMoveCampaign(campaign.id, null)}>
                                {t('campaignEdit.reorder.removeFromGroup')}
                              </Menu.Item>
                            )}
                            {availableGroups.length === 0 && !groupId && (
                              <Menu.Item disabled><Text size="xs" c="dimmed">{t('campaignEdit.reorder.noGroups')}</Text></Menu.Item>
                            )}
                          </Menu.Dropdown>
                        </Menu>
                      </Group>
                    </SortableItem>
                  </div>
                </div>
              </div>
            );
          })}
        </SortableContext>
      </DndContext>
    </Paper>
  );
});

export default CampaignReorder;
